/**
 * Smart Color Mockups — shared tint helpers for admin preview, live designer, and export.
 */

export type GarmentTintMode = 'multiply' | 'recolor' | 'none';

export interface GarmentTintLayerLike {
  url: string;
  top: number;
  left: number;
  width: number;
  height: number;
  z?: number;
  tintable?: boolean;
  tintMode?: GarmentTintMode;
  /** Zero-based segment from a semicolon-separated catalog color. Defaults to 0. */
  colorSlot?: number;
}

export interface ResolvedColorSlot {
  hex: string;
  requestedSlot: number;
  usedSlot: number;
  fallback: boolean;
}

function normalizeTintHexPart(value: string): string | null {
  const raw = value.trim().replace(/^#/, '');
  const expanded = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  return /^[0-9a-f]{6}$/i.test(expanded) ? expanded : null;
}

/** Valid, normalized segments from a semicolon/comma separated product color. */
export function parseTintHexParts(hex: string): string[] {
  return String(hex || '')
    .replace(/,/g, ';')
    .split(';')
    .map(normalizeTintHexPart)
    .filter((part): part is string => part !== null);
}

/**
 * Resolve a garment color slot without retaining state from a previous variant.
 * Missing slots always fall back to the active variant's primary segment.
 */
export function resolveColorSlot(hex: string, slot = 0): ResolvedColorSlot {
  const parts = String(hex || '').replace(/,/g, ';').split(';').map(normalizeTintHexPart);
  const requestedSlot = Number.isFinite(slot) ? Math.max(0, Math.floor(slot)) : 0;
  const primary = parts[0] ?? '000000';
  const hasRequested = requestedSlot < parts.length && parts[requestedSlot] !== null;
  return {
    hex: hasRequested ? parts[requestedSlot]! : primary,
    requestedSlot,
    usedSlot: hasRequested ? requestedSlot : 0,
    fallback: !hasRequested,
  };
}

/** Resolve a zero-based segment from a multi-HEX swatch; invalid/missing slots fall back to slot 0. */
export function tintHexAtSlot(hex: string, slot = 0): string {
  return resolveColorSlot(hex, slot).hex;
}

/** Equal conic wedges for a multi-HEX swatch. */
export function multiHexGradient(hex: string): string {
  const parts = parseTintHexParts(hex);
  if (parts.length <= 1) return `#${parts[0] ?? '000000'}`;
  const step = 100 / parts.length;
  const stops = parts.flatMap((part, index) => [
    `#${part} ${(index * step).toFixed(3)}%`,
    `#${part} ${((index + 1) * step).toFixed(3)}%`,
  ]);
  return `conic-gradient(from -90deg, ${stops.join(', ')})`;
}

/** Backward-compatible primary segment helper. */
export function primaryTintHex(hex: string): string {
  return tintHexAtSlot(hex, 0);
}

export function cssHexColor(hex: string): string {
  return `#${primaryTintHex(hex)}`;
}

export function shouldTintLayer(
  layer: GarmentTintLayerLike,
  colorTintEnabled: boolean,
): boolean {
  if (!colorTintEnabled) return false;
  if (layer.tintMode === 'none') return false;
  if (layer.tintable === false) return false;
  return true;
}

export function garmentLayerPositionStyle(
  layer: Pick<GarmentTintLayerLike, 'top' | 'left' | 'width' | 'height' | 'z'>,
  gIdx: number,
): Record<string, string | number> {
  return {
    top: `${layer.top ?? 0}%`,
    left: `${layer.left ?? 0}%`,
    width: `${layer.width ?? 100}%`,
    height: `${layer.height ?? 100}%`,
    zIndex:
      layer.z != null && !Number.isNaN(Number(layer.z))
        ? Number(layer.z)
        : 10 * (gIdx + 1),
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const h = primaryTintHex(hex);
  const full = h.length === 3
    ? h.split('').map((c) => c + c).join('')
    : h.padEnd(6, '0').slice(0, 6);
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

/** Pixel recolor (ImageColorizer-style): non-transparent pixels → solid hex, alpha preserved. */
export function recolorImageDataRegion(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  hex: string,
): void {
  const iw = Math.max(1, Math.floor(w));
  const ih = Math.max(1, Math.floor(h));
  const imageData = ctx.getImageData(Math.floor(x), Math.floor(y), iw, ih);
  const [r, g, b] = hexToRgb(hex);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] > 0) {
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
    }
  }
  ctx.putImageData(imageData, Math.floor(x), Math.floor(y));
}
